/**
 * Bewonersmeldingen — module-level singleton.
 *
 * Spec: specs/20260831-domotica-meldingen-bewonerstablet/spec.md FR-13, FR-29 t/m FR-32
 * Plan: §6.0
 * Taak: T-1.21
 *
 * ── WAAROM OP MODULENIVEAU EN NIET IN EEN CONTEXT ──────────────────────────
 *
 * Dezelfde reden als `useMqttCommands.ts`, dat er bewust ook zo uitziet: de verwerking mag
 * niet gekoppeld zijn aan de React-levenscyclus. Een re-render of een opruimende effect-
 * cleanup zou anders de poll of de wachtrij met bevestigingen kunnen doden, en dan mist een
 * bewoner meldingen zonder dat iets dat meldt. De hook eronder LEEST alleen.
 *
 * ── FASE 1 IS PULL, GEEN PUSH ───────────────────────────────────────────────
 *
 * Er wordt hier bewust nog niet op een MQTT/WS-commando geluisterd. Het commandotype
 * `resident_notification` komt in fase 2 (T-2.03); een oudere bundle die het niet kent,
 * negeert het — dat is het gewenste degradatiegedrag op een vloot met gemengde versies.
 * Tot die tijd is de GET het volledige transport, en dat werkt: de melding staat in de
 * database vóór er ook maar iets gepusht wordt (persist-first). De push is een versnelling.
 *
 * ── DE ACK IS DE ENIGE MANIER WAAROP `displayed` ONTSTAAT ──────────────────
 *
 * De server komt nooit verder dan `pushed`. Alleen deze code mag `displayed` /
 * `acknowledged` melden, en alleen nadat het scherm het daadwerkelijk getekend heeft. Een
 * ack bij ONTVANGST zou precies de leugen zijn die deze hele feature moet vermijden.
 *
 * ── CHROMIUM 84 IS DE ONDERGRENS ────────────────────────────────────────────
 *
 * Geen `AbortSignal.timeout` (gebruik `timeoutSignal()` — de native variant heeft hier al
 * twee features gebroken), geen `structuredClone`, geen `String.replaceAll`, geen
 * top-level await.
 */

import type {
  ResidentNotificationEnvelope,
  ResidentNotificationPayload,
  ResidentNotificationPriority,
  ResidentNotificationVoiceReason,
  ResidentNotificationVoiceSource,
} from '@caro/shared/types';
import { localizeNotification, localizedSpeechText } from './notificationLanguage';
import { isAppLanguage } from '@caro/shared/utils';
import { logEvent } from '../utils/debugLog';
import * as SoundCache from './SoundCache';
import { getAudioReadiness } from '../utils/audioReadiness';
import { requestAttention, claimsScreen } from './AttentionBridge';
import { KeepAliveBridge } from '../native/KeepAliveBridge';
import type { SpeakOutcome } from '../native/KeepAliveBridge';
import { NativeBridge } from '../native/NativeBridge';
import * as AudioQueue from './AudioQueue';
import { fileItem, bundledBlob } from './audioItems';
import * as VoiceCache from './VoiceCache';
import type { VoiceClipRef } from './VoiceCache';

/** Eén melding zoals de app hem bijhoudt. */
export interface StoredNotification {
  deliveryId: string;
  notification: ResidentNotificationPayload;
  /** Lokaal: heeft de bewoner hem geopend? Vult de badge. */
  read: boolean;
  /** Lokaal: is er al een `displayed`-ack de deur uit? Voorkomt herhaalde 409's. */
  displayedAckSent: boolean;
}

interface PendingAck {
  notificationId: string;
  deliveryId: string;
  state: 'displayed' | 'acknowledged' | 'actioned';
  actionId?: string;
  soundPlayed?: boolean;
  webviewVersion?: string;
  /**
   * Een achtergronditem kon niet getoond worden (T-3.09).
   *
   * Reist mee op een gewone ack in plaats van via een eigen endpoint: de server schrijft
   * hem naar `deliveries.last_error`, óók wanneer de staat zelf niet verandert. Zo wordt
   * "de afbeelding kwam niet door" een cijfer over de vloot in plaats van een klacht per
   * telefoon. De melding zelf blijft gewoon staan — de tekst IS de melding.
   */
  mediaError?: string;
  /**
   * De stem (spraakmeldingen fase 0, FR-0.4). Reist op een EIGEN `displayed`-ack, omdat de
   * stem pas na de chime(s) eindigt; de server schrijft hem ook als de staat niet verandert.
   */
  voicePlayed?: boolean;
  voiceSource?: ResidentNotificationVoiceSource;
  voiceReason?: ResidentNotificationVoiceReason;
  clientTimestamp: string;
}

/** Wat de hook aanlevert. De store kent geen auth en geen axios. */
export interface NotificationTransport {
  get: (url: string) => Promise<unknown>;
  post: (url: string, body: unknown) => Promise<unknown>;
  /**
   * De BYTES van een bestand, geauthenticeerd. Optioneel: zonder deze functie kan
   * `SoundCache` alleen bestanden halen die zonder toestel-token bereikbaar zijn, en de
   * geluidsroute is dat niet.
   */
  getBlob?: (url: string) => Promise<Blob | null>;
}

const STORAGE_KEY = 'caro_resident_notifications';
const ACK_STORAGE_KEY = 'caro_resident_notification_acks';
// ── WAT AL GETOOND IS, MOET EEN HERSTART OVERLEVEN ────────────────────────
//
// `dismissed` stond alleen in het geheugen terwijl de MELDINGEN in localStorage staan.
// Na een herlaad -- of zodra de poll een nog niet verlopen melding opnieuw ophaalt -- was
// de lijst er dus weer en de kennis 'deze is al getoond' verdwenen. De tablet liep dan de
// reeds bekeken meldingen opnieuw af en speelde ze nog eens af, met geluid en al.
// Gemeten in productie op 2026-09-08.
const DISMISSED_STORAGE_KEY = 'caro_resident_notification_dismissed';
// Genoeg voor een dag testen en ruim boven wat een bewoner op een dag krijgt. Zonder
// bovengrens groeit deze lijst nooit meer terug: hij wordt alleen aangevuld.
const DISMISSED_MAX = 200;
const POLL_INTERVAL_MS = 60_000;
const LIST_URL = '/api/v1/resident/notifications';

// ── Modulestatus (overleeft de React-levenscyclus) ─────────────────────────

let items: StoredNotification[] = [];
let pendingAcks: PendingAck[] = [];
let transport: NotificationTransport | null = null;
let pollTimer: ReturnType<typeof setInterval> | null = null;
/**
 * ÉÉN vervaltimer voor alle meldingen, niet één per melding.
 *
 * Met 30 openstaande meldingen zou dat 30 timers zijn die een WebView op een
 * 800x480-kiosk elk apart wakker maken. Deze wordt telkens op de EERSTVOLGENDE
 * vervaldatum gezet en daarna opnieuw berekend.
 */
let expiryTimer: ReturnType<typeof setTimeout> | null = null;

const listeners = new Set<() => void>();

function notify() {
  listeners.forEach((l) => {
    try {
      l();
    } catch {
      /* één luisteraar mag de rest niet slopen */
    }
  });
}

// ── Persistentie ───────────────────────────────────────────────────────────
//
// De lijstmodus moet een WebView-herstart overleven: een tablet die 's nachts zijn
// WebView vernieuwt mag de inbox niet leegmaken. localStorage kan gooien (privémodus,
// geblokkeerde site-data), dus elke aanraking zit in een try.

function persist() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    /* geen opslag beschikbaar — de poll vult de lijst na een herstart opnieuw */
  }
}

/** Welke meldingen al getoond en weggeklikt zijn. Overleeft een herstart -- zie
 *  DISMISSED_STORAGE_KEY hierboven. Gedeclareerd VOOR `restore()`, anders leest die een
 *  const die nog in zijn temporal dead zone staat. */
const dismissed = new Set<string>();

function persistDismissed() {
  try {
    // Nieuwste achteraan; bij overschrijding vallen de oudste weg. Een melding van gisteren
    // is toch verlopen, dus die hoeft niet meer tegengehouden te worden.
    const keep = Array.from(dismissed).slice(-DISMISSED_MAX);
    localStorage.setItem(DISMISSED_STORAGE_KEY, JSON.stringify(keep));
  } catch {
    /* geen opslag — dan kan een melding na een herstart nog eens langskomen */
  }
}

function persistAcks() {
  try {
    localStorage.setItem(ACK_STORAGE_KEY, JSON.stringify(pendingAcks));
  } catch {
    /* zie boven */
  }
}

function restore() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) items = parsed as StoredNotification[];
    }
  } catch {
    items = [];
  }
  try {
    const raw = localStorage.getItem(DISMISSED_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) for (const id of parsed) dismissed.add(String(id));
    }
  } catch {
    /* onleesbaar: dan begint de lijst leeg en kan er een melding herhaald worden */
  }
  try {
    const raw = localStorage.getItem(ACK_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) pendingAcks = parsed as PendingAck[];
    }
  } catch {
    pendingAcks = [];
  }
}

restore();

// ── Verval ─────────────────────────────────────────────────────────────────

function expiryOf(item: StoredNotification): number {
  const t = new Date(item.notification.expiresAt).getTime();
  // Een onleesbare datum telt als "verlopen": liever een melding te weinig dan een melding
  // die nooit meer weggaat.
  return Number.isFinite(t) ? t : 0;
}

/**
 * Gooi weg wat verlopen is en zet de timer op de eerstvolgende vervaldatum.
 *
 * Een verlopen melding wordt NIET alsnog getoond — ook niet als hij nog in localStorage
 * staat van vóór een herstart. "Deur open" van vier uur geleden op een tablet duwen is
 * erger dan hem verliezen.
 */
function pruneExpired() {
  const now = Date.now();
  const before = items.length;
  items = items.filter((i) => expiryOf(i) > now);
  if (items.length !== before) {
    persist();
    notify();
  }

  if (expiryTimer) {
    clearTimeout(expiryTimer);
    expiryTimer = null;
  }
  if (items.length === 0) return;

  let next = Infinity;
  for (const i of items) {
    const t = expiryOf(i);
    if (t < next) next = t;
  }
  if (!Number.isFinite(next)) return;
  // Minimaal 1s, en afgekapt op het polling-interval: setTimeout met een enorme delay is
  // op sommige WebViews onbetrouwbaar, en de poll komt toch wel langs.
  const delay = Math.max(1000, Math.min(next - now, POLL_INTERVAL_MS));
  expiryTimer = setTimeout(pruneExpired, delay);
}

// ── Ophalen ────────────────────────────────────────────────────────────────

function mergeFromServer(envelopes: ResidentNotificationEnvelope[]) {
  const byId = new Map<string, StoredNotification>();
  for (const existing of items) byId.set(existing.notification.notificationId, existing);

  for (const env of envelopes) {
    if (!env || !env.notification) continue;
    // Een onbekende payloadversie wordt genegeerd in plaats van half gerenderd. Een tablet
    // die iets toont wat hij niet begrijpt, is erger dan een tablet die niets toont.
    if (env.notification.v !== 1) continue;

    const id = env.notification.notificationId;
    const prev = byId.get(id);
    byId.set(id, {
      deliveryId: env.deliveryId,
      notification: env.notification,
      // De server kent `read` niet — dat is een lokale toestand van dit toestel. Bij een
      // update van dezelfde melding blijft hij dus staan.
      read: prev ? prev.read : false,
      displayedAckSent: prev ? prev.displayedAckSent : false,
    });
  }

  items = Array.from(byId.values()).sort(
    (a, b) =>
      new Date(b.notification.issuedAt).getTime() - new Date(a.notification.issuedAt).getTime(),
  );
  persist();
  notify();
  pruneExpired();
}

/**
 * Haal openstaande meldingen op.
 *
 * Werpt nooit. Een tablet zonder netwerk is een normale toestand — de vorige lijst blijft
 * gewoon staan, en dat is precies waarom hij in localStorage bewaard wordt.
 */
export async function refresh(): Promise<void> {
  if (!transport) return;
  // Eerst de openstaande bevestigingen: die zijn ouder en de server werkt er zijn
  // delivery-staten mee bij, wat het antwoord op de GET beïnvloedt.
  await flushAcks();
  try {
    const res: any = await transport.get(LIST_URL);
    const list = res && Array.isArray(res.notifications) ? res.notifications : [];
    mergeFromServer(list as ResidentNotificationEnvelope[]);
  } catch {
    /* offline of 500 — de bestaande lijst blijft staan, de volgende poll probeert opnieuw */
  }
}

// ── Bevestigen ─────────────────────────────────────────────────────────────

/**
 * De WebView-versie, zodat de vloot MEETBAAR wordt in plaats van geschat.
 *
 * Bewust geen sluitende parser: alleen het Chrome/Chromium-versienummer, want dat is de
 * enige vraag die telt (de ondergrens is 84).
 */
function webviewVersion(): string | undefined {
  try {
    const m = /Chrom(?:e|ium)\/(\d+)/.exec(navigator.userAgent);
    return m ? m[1] : undefined;
  } catch {
    return undefined;
  }
}

function queueAck(ack: PendingAck) {
  // Dedup op (delivery, state): dezelfde ack twee keer in de wachtrij levert een 200 en een
  // 409, en dat tweede antwoord zou als fout gelezen worden.
  //
  // ⚠ Een ack MÉT `mediaError` telt als een andere ack dan dezelfde zonder. Anders zou een
  // mediafout die ná de `displayed`-ack optreedt — het normale geval, want de afbeelding
  // faalt pas nadat het scherm getekend is — stilzwijgend weggededuped worden en nooit op
  // `deliveries.last_error` belanden.
  //
  // Om dezelfde reden telt een ack MÉT stemvelden als een andere dan dezelfde zonder: de stem
  // komt bijna altijd ná de `displayed`-ack van het geluid (fase 0, FR-0.4).
  const already = pendingAcks.some(
    (a) => a.deliveryId === ack.deliveryId
      && a.state === ack.state
      && !!a.mediaError === !!ack.mediaError
      && (a.voicePlayed === undefined) === (ack.voicePlayed === undefined),
  );
  if (!already) {
    pendingAcks = pendingAcks.concat([ack]);
    persistAcks();
  }
  void flushAcks();
}

/**
 * Stuur de wachtende bevestigingen.
 *
 * ── EEN 409 IS GEEN FOUT OM OPNIEUW TE PROBEREN ────────────────────────────
 *
 * De server accepteert alleen VOORWAARTSE overgangen. Een 409 betekent dat de melding al
 * verder staat — bijvoorbeeld omdat de tweede tablet in dezelfde unit hem al beantwoord
 * heeft. Die ack blijft dus niet in de wachtrij hangen; hij is klaar. Alleen een
 * netwerkfout wordt opnieuw geprobeerd, anders groeit de wachtrij eeuwig door.
 *
 * ── ÉÉN RONDE TEGELIJK ─────────────────────────────────────────────────────
 *
 * Sinds de stem een eigen ack heeft (fase 0) komen er vaak twee acks vlak na elkaar. Twee
 * gelijktijdige rondes stuurden dezelfde ack dan twee keer, en de ronde die als laatste
 * klaar was, overschreef de wachtrij — met wat de andere net had toegevoegd erbij weg. Nu
 * loopt er hoogstens één ronde; een aanroep tijdens een ronde vraagt er één na.
 */
let flushing: Promise<void> | null = null;
let flushAgain = false;

export function flushAcks(): Promise<void> {
  if (flushing) {
    flushAgain = true;
    return flushing;
  }
  flushing = (async () => {
    try {
      do {
        flushAgain = false;
        // eslint-disable-next-line no-await-in-loop
        await flushOnce();
      } while (flushAgain && transport && pendingAcks.length > 0);
    } finally {
      flushing = null;
    }
  })();
  return flushing;
}

async function flushOnce(): Promise<void> {
  if (!transport || pendingAcks.length === 0) return;
  const queue = pendingAcks.slice(0);
  const keep: PendingAck[] = [];

  for (const ack of queue) {
    try {
      await transport.post(
        `${LIST_URL}/${encodeURIComponent(ack.notificationId)}/ack`,
        {
          deliveryId: ack.deliveryId,
          state: ack.state,
          actionId: ack.actionId,
          soundPlayed: ack.soundPlayed,
          webviewVersion: ack.webviewVersion,
          mediaError: ack.mediaError,
          voicePlayed: ack.voicePlayed,
          voiceSource: ack.voiceSource,
          voiceReason: ack.voiceReason,
          clientTimestamp: ack.clientTimestamp,
        },
      );
    } catch (err: any) {
      const status = err && err.response && err.response.status;
      // 401/403: deze tablet is niet (meer) aangemeld. De ack zelf is niet fout — bewaar
      // hem en de rest van de wachtrij, maar stuur ze nu niet één voor één de deur uit:
      // dat waren extra kale verzoeken per poll (observability #29).
      if (status === 401 || status === 403) {
        keep.push(...queue.slice(queue.indexOf(ack)));
        break;
      }
      // 409 = al verder; 404 = weg of niet van ons; 400 = deze ack klopt niet; 422 = een
      // stemveld met een waarde die de server niet kent (fase 0). Alle vier definitief.
      // Alleen "geen antwoord" of een 5xx is het opnieuw proberen waard.
      const permanent = status === 409 || status === 404 || status === 400 || status === 422;
      if (!permanent) keep.push(ack);
    }
  }

  // Wat er TIJDENS deze ronde bijkwam, blijft staan voor de volgende.
  const added = pendingAcks.filter((a) => queue.indexOf(a) === -1);
  pendingAcks = keep.concat(added);
  persistAcks();
}

/**
 * De melding is DAADWERKELIJK op het scherm getekend.
 *
 * ⚠ Roep dit nooit aan bij ontvangst van een melding — alleen nadat de browser hem echt
 * geschilderd heeft (na `requestAnimationFrame`). Dat onderscheid is de hele reden dat
 * `displayed` naast `pushed` bestaat.
 */
export function markDisplayed(
  notificationId: string,
  soundPlayed?: boolean,
  soundError?: string,
) {
  const item = items.find((i) => i.notification.notificationId === notificationId);
  if (!item || item.displayedAckSent) return;
  item.displayedAckSent = true;
  persist();
  queueAck({
    notificationId,
    deliveryId: item.deliveryId,
    state: 'displayed',
    soundPlayed,
    // ⚠ Alleen bij een MISLUKKING. `soundPlayed: true` met een reden erbij zou de vraag
    // "waarom klonk het niet?" onbeantwoordbaar maken op precies de toestellen waar het wél
    // klonk — de vorige keer dat twee betekenissen in één veld gepropt werden.
    mediaError: soundError,
    webviewVersion: webviewVersion(),
    clientTimestamp: new Date().toISOString(),
  });
}

/**
 * T-3.09 — een achtergronditem kon niet getoond worden.
 *
 * ⚠ Wat hier NIET gebeurt is net zo belangrijk als wat er wel gebeurt: de melding
 * verdwijnt niet, degradeert niet, en wordt niet als mislukt gemarkeerd. De tekst IS de
 * melding; de achtergrond is decoratie. Wat er wel gebeurt is dat het falen op
 * `deliveries.last_error` belandt, zodat te MÉTEN valt hoe vaak dit gebeurt in plaats van
 * erover te speculeren — dezelfde les als bij `soundPlayed`.
 *
 * Hoogstens één per melding per sessie: acht kapotte items in één carousel zijn acht keer
 * hetzelfde bericht, en de eerste is de bruikbare.
 */
const mediaErrorsReported = new Set<string>();

export function reportMediaError(notificationId: string, message: string) {
  if (!notificationId || !message) return;
  if (mediaErrorsReported.has(notificationId)) return;
  const item = items.find((i) => i.notification.notificationId === notificationId);
  if (!item) return;
  mediaErrorsReported.add(notificationId);
  queueAck({
    notificationId,
    deliveryId: item.deliveryId,
    // `displayed` en niet iets nieuws: de melding IS getoond — alleen een decoratief item
    // niet. Een eigen staat zou suggereren dat er iets mis is met de aflevering.
    state: 'displayed',
    mediaError: String(message).slice(0, 500),
    webviewVersion: webviewVersion(),
    clientTimestamp: new Date().toISOString(),
  });
}

/** De bewoner heeft de melding geopend. */
export function markAcknowledged(notificationId: string) {
  const item = items.find((i) => i.notification.notificationId === notificationId);
  if (!item) return;
  if (item.read) return;
  item.read = true;
  persist();
  notify();
  queueAck({
    notificationId,
    deliveryId: item.deliveryId,
    state: 'acknowledged',
    webviewVersion: webviewVersion(),
    clientTimestamp: new Date().toISOString(),
  });
}

// ── Levenscyclus ───────────────────────────────────────────────────────────

/**
 * Koppel de API-laag en start de poll. Idempotent: een tweede aanroep vervangt alleen de
 * transportlaag en start geen tweede timer.
 */
export function configure(t: NotificationTransport) {
  transport = t;
  // SoundCache haalt zijn bytes langs dezelfde geauthenticeerde weg. Zonder dit stuurt hij
  // een kale fetch en krijgt hij 401 op de geluidsroute.
  SoundCache.setFetcher(t.getBlob ? (url) => t.getBlob!(url) : null);
  void refresh();
  if (!pollTimer) {
    pollTimer = setInterval(() => {
      void refresh();
    }, POLL_INTERVAL_MS);
  }
  pruneExpired();
  // T-2.13 — de geluiden van deze unit vooraf in de cache zetten.
  //
  // ⚠ Bewust NIET geawait en bewust zonder foutafhandeling die iets doet: voorladen is
  // comfort. Mislukt het volledig, dan haalt de eerste melding zijn geluid alsnog over het
  // netwerk (stap 2 van de vervalladder) of valt terug op een ingebouwd geluid. Er mag geen
  // enkele weg zijn waarlangs een audiodownload de inbox tegenhoudt.
  void preloadSounds();
}

/**
 * Ontkoppel de API-laag en stop de poll. Aangeroepen zodra de tablet niet meer
 * aangemeld is.
 *
 * Zonder dit liep de poll na een uitlog gewoon door met de oude transportlaag: elke 60 s
 * een GET zonder Authorization-header, die de API als waarschuwing logde — 6248 keer, van
 * een tablet die zelf niets liet merken (observability #29). De lijst en de wachtende
 * bevestigingen blijven bewaard; `configure()` pakt ze na een nieuwe aanmelding weer op.
 */
export function stop() {
  transport = null;
  if (pollTimer) clearInterval(pollTimer);
  pollTimer = null;
  SoundCache.setFetcher(null);
}

const SOUNDS_URL = '/api/v1/resident/notification-sounds';

async function preloadSounds(): Promise<void> {
  if (!transport) return;
  try {
    const res: any = await transport.get(SOUNDS_URL);
    const list = res && Array.isArray(res.sounds) ? res.sounds : [];
    if (list.length) await SoundCache.preload(list);
  } catch {
    /* geen netwerk of geen fase-2-migratie — de ladder vangt het op */
  }
}

// ── Lezen ──────────────────────────────────────────────────────────────────

export function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

/**
 * ⚠ Geeft dezelfde array-referentie terug tot er iets verandert.
 *
 * `useSyncExternalStore` vergelijkt op identiteit; een nieuwe array per aanroep geeft een
 * oneindige renderlus. Elke mutatie hierboven vervangt `items` door een nieuwe array, dus
 * de identiteit verandert precies wanneer de inhoud dat doet.
 */
export function getSnapshot(): StoredNotification[] {
  return items;
}

export function getUnreadCount(): number {
  let n = 0;
  const now = Date.now();
  for (const i of items) {
    if (!i.read && expiryOf(i) > now) n += 1;
  }
  return n;
}

/** Alleen voor tests. */
export function __reset() {
  items = [];
  pendingAcks = [];
  transport = null;
  if (pollTimer) clearInterval(pollTimer);
  if (expiryTimer) clearTimeout(expiryTimer);
  pollTimer = null;
  expiryTimer = null;
  flushing = null;
  flushAgain = false;
  listeners.clear();
  __resetPhase2();
}

// ═══════════════════════════════════════════════════════════════════════════
// FASE 2 — push, schermclaim en geluid
// ═══════════════════════════════════════════════════════════════════════════

/**
 * ── T-2.04: DEDUP OP `notificationId`, NAAST DE DEDUP OP `cmd.id` ──────────
 *
 * `useMqttCommands` dedupt al op het commando-id, maar WS en MQTT dragen dezelfde melding
 * in TWEE commando's met elk hun eigen id. Zonder deze tweede laag geeft een melding die
 * over beide kanalen aankomt twee popups — en de bewoner klikt er dan één weg en denkt
 * klaar te zijn.
 *
 * Ook de poll uit fase 1 komt hier langs: `mergeFromServer` en `ingest` schrijven in
 * dezelfde `items`, op dezelfde sleutel. Er is dus één lijst, geen tweede waarheid.
 */

/** Meldingen die deze sessie al zijn weggeklikt. Voorkomt dat een poll ze terugtovert. */

/**
 * De melding die op dit moment het scherm mag claimen.
 *
 * ⚠ ÉÉN tegelijk. Een wachtrij van dialogen op een wandtablet is een tablet die zijn
 * bewoner gijzelt tot hij alles heeft weggeklikt. Bij gelijktijdigheid wint de hoogste
 * prioriteit; de rest blijft gewoon in de lijst staan en telt mee in de badge.
 */
let claimed: StoredNotification | null = null;

const PRIORITY_RANK: Record<ResidentNotificationPriority, number> = {
  urgent: 3,
  high: 2,
  normal: 1,
  low: 0,
};

/** Mag deze melding het scherm overnemen? `low` nooit, `list` nooit. */
function wantsScreen(item: StoredNotification): boolean {
  const n = item.notification;
  if (n.presentation === 'list') return false;
  if (!claimsScreen(n.priority)) return false;
  if (dismissed.has(n.notificationId)) return false;
  return expiryOf(item) > Date.now();
}

function reconsiderClaim() {
  let best: StoredNotification | null = null;
  for (const item of items) {
    if (!wantsScreen(item)) continue;
    if (
      !best
      || PRIORITY_RANK[item.notification.priority] > PRIORITY_RANK[best.notification.priority]
    ) {
      best = item;
    }
  }

  // ── T-3.05: wie er staat, blijft staan bij gelijke prioriteit ────────────
  //
  // `items` is gesorteerd op nieuw→oud, dus zonder deze regel zou elke nieuwe melding van
  // DEZELFDE prioriteit het scherm overnemen van degene die er al staat. Bij een dialoog is
  // dat hinderlijk; bij een volledig scherm is het een tablet waarvan de inhoud onder de
  // vinger van de bewoner verwisselt, precies terwijl hij "Sluiten" aanraakt.
  //
  // Alleen een STRIKT hogere prioriteit onderbreekt. De rest wacht tot dit scherm vrijkomt
  // en staat intussen gewoon in de lijst, meegeteld in de badge — geen wachtrij van
  // volledige schermen, en ook niets verloren.
  //
  // ⚠ De vergelijking loopt op `notificationId`, NIET op objectidentiteit. `ingest` vervangt
  // bij een `replace` het hele item door een nieuw object (dat moet, anders ziet
  // `useSyncExternalStore` de wijziging niet). Zou deze regel op identiteit vergelijken, dan
  // zou een verse versie van de MELDING DIE ER AL STAAT als "een andere melding van gelijke
  // prioriteit" gelden en geweigerd worden — en bleef de tablet de oude tekst tonen. Precies
  // het soort verschil dat pas opvalt wanneer een brandmelding een verouderde locatie noemt.
  if (
    best
    && claimed
    && best.notification.notificationId !== claimed.notification.notificationId
    && wantsScreen(claimed)
  ) {
    const stillThere = items.indexOf(claimed) !== -1;
    if (
      stillThere
      && PRIORITY_RANK[best.notification.priority] <= PRIORITY_RANK[claimed.notification.priority]
    ) {
      best = claimed;
    }
  }

  if (best !== claimed) {
    claimed = best;
    notify();
  }
}

/** De melding die nu op het scherm hoort te staan, of `null`. */
export function getClaimedSnapshot(): StoredNotification | null {
  return claimed;
}

/**
 * Een melding kwam binnen via WS of MQTT.
 *
 * Persist-first betekent dat de rij al in de database staat vóór dit commando verstuurd
 * werd; deze weg is dus een VERSNELLING. Mislukt hij volledig, dan levert de poll de
 * melding alsnog — met hooguit een minuut vertraging, en zonder dubbele popup dankzij de
 * dedup hierboven.
 */
export function ingest(payload: ResidentNotificationPayload, deliveryId?: string): void {
  if (!payload || payload.v !== 1 || !payload.notificationId) return;

  const id = payload.notificationId;
  // Al weggeklikt op dit toestel? Dan is een tweede push van hetzelfde bericht geen reden
  // om hem opnieuw voor iemands neus te zetten.
  if (dismissed.has(id)) return;

  const existing = items.find((i) => i.notification.notificationId === id);
  if (existing) {
    // Een `replace` van de server-side dedupe (T-2.08): dezelfde melding, verse inhoud.
    // `read` en `displayedAckSent` blijven staan — dat is de toestand van DIT toestel.
    //
    // ⚠ Een NIEUW object, geen mutatie van het bestaande. `useSyncExternalStore` vergelijkt
    // op IDENTITEIT: een gemuteerd object is dezelfde referentie, en dan blijft de dialoog
    // de oude tekst tonen terwijl de server allang iets anders gestuurd heeft. Precies het
    // soort verschil dat pas opvalt wanneer een brandmelding een verouderde locatie noemt.
    const updated: StoredNotification = {
      deliveryId: deliveryId || payload.deliveryId || existing.deliveryId,
      notification: payload,
      read: existing.read,
      displayedAckSent: existing.displayedAckSent,
    };
    items = items.map((i) => (i.notification.notificationId === id ? updated : i));
  } else {
    items = [
      {
        deliveryId: deliveryId || payload.deliveryId,
        notification: payload,
        read: false,
        displayedAckSent: false,
      },
      ...items,
    ];
  }

  persist();
  notify();
  pruneExpired();

  // De stand-bylaag pas ná het opslaan aanspreken: gooit `wakeScreen()`, dan staat de
  // melding toch al veilig in de lijst.
  requestAttention(payload.priority);
  if (payload.priority === 'urgent') {
    void KeepAliveBridge.wakeScreen();
    void KeepAliveBridge.bringToFront();
  }

  reconsiderClaim();
}

/**
 * T-2.09 — de tegenhanger: `resident_notification_clear`.
 *
 * Nodig omdat "brand gedetecteerd" moet kunnen verdwijnen zodra de flow ziet dat de input
 * weer laag is. Niets gevonden is GEEN fout: de melding kan al verlopen zijn, al zijn
 * weggeklikt, of dit toestel heeft hem nooit gehad.
 *
 * ⚠ Intrekken op `dedupeKey` gebeurt op de SERVER, niet hier: die sleutel staat bewust niet
 * in de payload naar de tablet (het is een serverbegrip, en een tablet die hem kent kan er
 * meldingen mee opzoeken die niet van hem zijn). De server lost hem op en stuurt de
 * concrete `notificationId`s mee.
 */
export function clearNotification(opts: { notificationId?: string }): number {
  const notificationId = opts && opts.notificationId;
  if (!notificationId) return 0;

  const before = items.length;
  items = items.filter((i) => i.notification.notificationId !== notificationId);
  const removed = before - items.length;
  if (removed > 0) {
    dismissed.add(notificationId);
    persistDismissed();
    persist();
    notify();
    reconsiderClaim();
  }
  return removed;
}

/**
 * De bewoner heeft de melding weggeklikt (of de aftelling liep af).
 *
 * `acknowledged` is een echte ack en gaat naar de server; het scherm geeft hij hoe dan ook
 * vrij, ook als die ack in de offline-wachtrij blijft hangen. Anders zou een tablet zonder
 * netwerk een dialoog niet meer kwijtraken.
 */
export function dismissClaimed(notificationId: string, acknowledge = true): void {
  dismissed.add(notificationId);
  persistDismissed();
  stopSound();
  // De stem stopt mee. Een melding die weg is en toch nog voorgelezen wordt, laat een
  // bewoner zoeken naar iets wat niet meer op het scherm staat.
  void stopTts();
  if (acknowledge) markAcknowledged(notificationId);

  // ── EEN VOLLEDIG SCHERM IS EEN MOMENT, GEEN BERICHT ───────────────────────
  //
  // Een `fullpage` onderbreekt: hij neemt het scherm over omdat er NU iets gebeurt. Is
  // dat moment voorbij, dan is de melding voorbij. Hem daarna nog als ongelezen op de
  // startpagina laten staan is de bewoner twee keer hetzelfde vertellen -- en bij een
  // deurbel gaat het over iemand die inmiddels weg is.
  //
  // Een `list`-melding blijft WEL staan: die is juist bedoeld om later te lezen. `dialog`
  // zit ertussenin en blijft eveneens; die is aan te klikken en hoort dus vindbaar te
  // blijven tot iemand dat doet.
  //
  // Dit raakt alleen wat de TABLET toont. De rij op de server blijft staan met zijn eigen
  // vervaltijd, dus het spoor 'deze melding is verstuurd en getoond' verandert niet -- en
  // `acknowledged` blijft voorbehouden aan een bewoner die werkelijk iets bevestigd heeft.
  const item = items.find((i) => i.notification.notificationId === notificationId);
  if (item && item.notification.presentation === 'fullpage') {
    items = items.filter((i) => i.notification.notificationId !== notificationId);
    persist();
    notify();
  }

  reconsiderClaim();
}

// ── Voorlezen (fase 5) ─────────────────────────────────────────────────────

/**
 * ⚠ TTS IS EEN AANVULLING, NOOIT DE ENIGE DRAGER (T-5.05).
 *
 * Een dove bewoner mag niets missen. Alles wat hier gebeurt staat NAAST de visuele melding
 * en verandert er niets aan: de titel, de tekst, de achtergrond en de knoppen worden
 * getekend voordat er ook maar één woord klinkt, en ze blijven staan wanneer er niets
 * klinkt. Er is geen pad waarlangs een mislukte of geblokkeerde stem iets van het scherm
 * haalt.
 *
 * ── EN HIJ ERFT DEZELFDE AUTOPLAY-BEPERKING ALS GELUID ─────────────────────
 *
 * De audio-activatiebanner uit de ringtone-mitigatie van 2026-07-23 BESTAAT NIET MEER — hij
 * is met de terugrol van 2026-09-01 uit de app verdwenen; alleen z-index 9980 staat
 * gereserveerd. Op web kan `speechSynthesis` dus stil geweigerd worden zonder dat iemand het
 * merkt. Die banner wordt hier BEWUST niet herbouwd: hij hoort bij het ringtone-traject en
 * een tweede, half werkende kopie zou dat traject in de weg zitten. Wat hier telt is dat een
 * geweigerde stem de melding niet leeg achterlaat, en dat is hierboven geregeld.
 */

/**
 * De taal van deze bewoner, aangeleverd door de hook.
 *
 * De store kent geen auth, en de taal staat in `user.person.data.gameLanguage`. `'auto'` in
 * de payload betekent "de taal van de unit/persoon" (T-5.02), en dat is precies dit.
 */
let ttsLanguage = 'nl';

/** Welke melding er op dit moment voorgelezen wordt. `null` = stilte. */
let speakingFor: string | null = null;

export function setTtsLanguage(lang: string | null | undefined) {
  if (!lang || String(lang) === ttsLanguage) return;
  ttsLanguage = String(lang);
  // 20261016-taal-bewonersapp (wens 4): een melding die al op het scherm staat, toont na een
  // taalwissel de tekst in de nieuwe taal. Alle talen zitten in de payload; een seintje
  // aan de luisteraars volstaat, er is geen nieuw verzoek nodig.
  notify();
}

/** De huidige taal van deze bewoner, voor de keuze van de tekst (T033). */
function currentLanguage(): 'nl' | 'en' | 'fr' | 'de' {
  return isAppLanguage(ttsLanguage) ? ttsLanguage : 'nl';
}

/**
 * R-4: een tablet zonder stem voor deze taal leest niet voor. Dat mag geen stille fout zijn:
 * één waarschuwing in de console en een regel in de diagnoselog (T041 kijkt die na). Alleen
 * in de browser na te gaan; de native weg logt `tts:silent` met `langResult` zelf.
 */
function warnIfNoVoice(lang: string): void {
  try {
    const synth = typeof window !== 'undefined' ? window.speechSynthesis : undefined;
    const voices = synth && typeof synth.getVoices === 'function' ? synth.getVoices() : [];
    if (!voices || voices.length === 0) return; // nog niet geladen: niets te zeggen
    const prefix = String(lang).toLowerCase().slice(0, 2);
    if (!voices.some((v) => String(v.lang || '').toLowerCase().startsWith(prefix))) {
      console.warn('[TTS] no voice', lang);
      logEvent('tts.noVoice', { lang });
    }
  } catch {
    /* nooit de melding laten vallen op een diagnose */
  }
}

/**
 * T-5.04 — de wachtrij is ÉÉN, en de nieuwste wint.
 *
 * ⚠ Twee stemmen tegelijk is geen "wat rommelig": het is twee onverstaanbare stemmen. Op een
 * handsfree wandtablet komt alles uit dezelfde luidspreker, en een bewoner die twee
 * meldingen tegelijk hoort, verstaat er nul. Een WACHTRIJ zou het ook niet oplossen — dan
 * hoort hij eerst het oude bericht en pas daarna het dringende.
 *
 * Vandaar: de nieuwe onderbreekt de lopende. Dat is dezelfde regel als de schermclaim, en om
 * dezelfde reden.
 */
export async function speakFor(payload: ResidentNotificationPayload): Promise<boolean> {
  const tts = payload && payload.tts;
  if (!tts || !tts.text) return false;

  // Eerst stoppen wat er klinkt, dan pas beginnen. Andersom overlappen ze een fractie, en
  // op een trage TTS-engine is die fractie een halve zin.
  await stopTts();
  const outcome = await speakNow(payload);
  return outcome.spoken;
}

/**
 * Spreek uit, zonder eerst iets te stoppen. De wachtrij (`presentAndSound`) roept dit aan:
 * zij zorgt er zelf voor dat er niets anders klinkt, en een `stopTts()` hier zou de eigen
 * reeks afbreken.
 *
 * Werpt nooit; het antwoord zegt of er gesproken werd en zo niet, waarom (FR-0.4).
 */
/**
 * Wat de tablet gaat uitspreken, en in welke taal.
 *
 * 20261016-taal-bewonersapp (FR-18): voorlezen in de taal van de GEKOZEN tekst, met de tekst
 * van die taal — wat er gezegd wordt is wat er staat. Een vaste taal in het blok wint. Een
 * payload van een oudere API (zonder `baseLanguage` en zonder vertalingen) weet niet in welke
 * taal hij staat; daar blijft `auto` de taal van de bewoner (T-5.02).
 *
 * Gedeeld met de clipkeuze (fase 1): een clip speelt alleen als zijn taal DEZE taal is.
 */
function speechTarget(payload: ResidentNotificationPayload): { text: string; lang: string } | null {
  const tts = payload && payload.tts;
  if (!tts || !tts.text) return null;
  const declared = isAppLanguage(payload.baseLanguage) || Boolean(payload.translations)
    || Boolean(payload.messageKey);
  const chosen = declared ? localizeNotification(payload, currentLanguage()) : null;
  const text = chosen ? localizedSpeechText(chosen) || tts.text : tts.text;
  const autoLang = chosen ? chosen.lang : ttsLanguage;
  const lang = !tts.lang || tts.lang === 'auto' ? autoLang : tts.lang;
  return { text, lang };
}

async function speakNow(payload: ResidentNotificationPayload): Promise<SpeakOutcome> {
  const target = speechTarget(payload);
  if (!target) return { spoken: false, reason: 'error' };
  const { text, lang } = target;
  warnIfNoVoice(lang);
  speakingFor = payload.notificationId;
  try {
    const result = await KeepAliveBridge.speak(text, lang);
    // Een oudere brug (of een testdubbel) geeft niets terug: dat is "gesproken", zoals vroeger.
    return result && typeof result.spoken === 'boolean' ? result : { spoken: true };
  } catch {
    // `KeepAliveBridge.speak` vangt zijn eigen fouten al af; dit is het tweede slot. Een
    // stem die niet komt, verandert niets aan wat er op het scherm staat.
    return { spoken: false, reason: 'error' };
  } finally {
    if (speakingFor === payload.notificationId) speakingFor = null;
  }
}

/** Stop wat er gezegd wordt. Ook aangeroepen bij een binnenkomende oproep (T-5.03). */
export async function stopTts(): Promise<void> {
  speakingFor = null;
  // Ook de reeksen in de wachtrij: anders begint de stem alsnog zodra de chime klaar is.
  stopSound();
  try {
    await KeepAliveBridge.stopTts();
  } catch {
    /* niets te stoppen */
  }
}

/** Alleen voor tests en voor de diagnose-overlay. */
export function getSpeakingFor(): string | null {
  return speakingFor;
}

// ── Actieknoppen (fase 4) ──────────────────────────────────────────────────

/**
 * T-4.05 / T-4.06 — een serveractie (`domotics_output` of `automation_event`).
 *
 * ── DE TABLET STUURT ALLEEN EEN ID ─────────────────────────────────────────
 *
 * (FR-22.) `deliveryId` en `actionId`, en verder niets. De output en de waarde staan in de
 * opgeslagen melding op de server; die worden daar opgehaald. Een verzoek waarin de output
 * genoemd staat, is een verzoek waarin iemand hem kan veranderen — en het verschil tussen
 * de keukenlamp en het deurslot is dan één cijfer.
 *
 * ── EN HET ANTWOORD WORDT GELOOFD, NIET AANGENOMEN ─────────────────────────
 *
 * (T-4.08.) `mqttService.publish` op de server RESOLVET `false` in plaats van te gooien;
 * de endpoint controleert die retourwaarde en antwoordt dan met 502
 * `command_not_delivered`. Die uitkomst komt hier terug als `ok: false`, zodat de knop een
 * zichtbare foutstaat krijgt. Geen groen vinkje op een commando dat niet aankwam.
 *
 * Werpt nooit: de aanroeper is een knop, en een knop die een exception opgooit laat een
 * bewoner achter met een scherm dat niet meer reageert.
 */
export async function invokeServerAction(
  notificationId: string,
  actionId: string,
): Promise<{ ok: boolean; code?: string }> {
  if (!transport) return { ok: false, code: 'offline' };
  const item = items.find((i) => i.notification.notificationId === notificationId);
  if (!item) return { ok: false, code: 'not_found' };

  try {
    const res: any = await transport.post(
      `${LIST_URL}/${encodeURIComponent(notificationId)}/action`,
      { deliveryId: item.deliveryId, actionId },
    );
    // Een 200 met `success: false` is óók een mislukking. De server gebruikt die vorm niet
    // voor deze route, maar hem hier als succes lezen zou betekenen dat één wijziging aan
    // de andere kant stilzwijgend een groen vinkje oplevert.
    if (res && res.success === false) return { ok: false, code: res.code || 'failed' };
    return { ok: true };
  } catch (err: any) {
    const status = err && err.response && err.response.status;
    const code = (err && err.response && err.response.data && err.response.data.code)
      || (status === 404 ? 'not_found' : 'failed');
    return { ok: false, code };
  }
}

// ── Geluid en stem: één reeks in de wachtrij ───────────────────────────────
//
// Spraakmeldingen fase 0 (specs/20261020-spraakmeldingen-elevenlabs FR-0.1, T046). Tot dan
// startte `presentAndSound()` de chime en de stem tegelijk, en bij `soundRepeat: 2` klonk de
// chime twee keer ÓVER de zin. Nu zet elke melding één REEKS in `AudioQueue`: chime ×
// `soundRepeat`, dan de stem. Tijdens een gesprek speelt de wachtrij niets; dat komt in de
// acks terug als `muted_call`.
//
// ⚠ Alle meldingen gaan op prioriteit `default`. De payload zegt niet of een melding een
// deurbel is, en `urgent` naar `alarm` tillen zou de alarmfases (fase 3) hun voorrang afnemen.
// Gelijke prioriteit WACHT: een tweede melding wordt na de eerste voorgelezen, niet erdoorheen.

/** De lopende of wachtende reeks per melding. */
const sequences = new Map<string, AudioQueue.AudioSequenceHandle>();
/** Stemuitkomsten die al gemeld zijn — één per melding per sessie. */
const voiceReported = new Set<string>();

/**
 * Stop wat er klinkt: chime én stem van ELKE melding in de wachtrij.
 *
 * Ook aangeroepen bij het sluiten van een dialoog en bij een binnenkomend gesprek. De
 * videofoonaankondiging zit ook in de wachtrij maar is van de deurbelpagina, niet van een
 * melding, en blijft dus staan.
 */
export function stopSound(): void {
  const handles = Array.from(sequences.values());
  sequences.clear();
  for (const h of handles) h.cancel();
}

/**
 * ⚠ T-2.14 — DIT WORDT NA HET RENDEREN AANGEROEPEN, NOOIT ERVOOR.
 *
 * De melding staat al op het scherm wanneer het geluid begint. Alles hierin — de
 * cachelookup, de netwerkophaal van maximaal 3 seconden, het afspelen zelf — gebeurt
 * daarna. Een melding die wacht op een audiodownload is een melding die op een trage
 * verbinding te laat komt, en dat is precies het gedrag dat FR-27 uitsluit.
 */
/**
 * WAAROM HET GELUID NIET KLONK — en waarom dat drie verschillende dingen zijn.
 *
 * `playFor` gaf voor elke mislukking hetzelfde `false` terug. Daardoor was "de bytes waren
 * niet op te halen" niet te onderscheiden van "de browser weigerde af te spelen", en die
 * twee vragen om tegenovergestelde oplossingen: de eerste is een server-/authprobleem, de
 * tweede is autoplaybeleid en lost geen enkele serverwijziging op. Zonder dat onderscheid
 * levert elke meting hetzelfde nietszeggende antwoord op — precies waar we op vastliepen.
 *
 * De reden reist mee in de `displayed`-ack als `mediaError` en belandt op
 * `deliveries.last_error`. Dat veld is het diagnosekanaal van deze aflevering; de prefix
 * `sound:` houdt hem onderscheidbaar van een mislukt achtergronditem.
 */
let lastSoundFailure: string | null = null;

/** De uitkomst van de opstartprobe, kort genoeg om in een foutregel te passen. */
function audioReadinessTag(): string {
  const r = getAudioReadiness();
  if (!r) return 'probe=niet-gedraaid';
  return `htmlAudio=${r.htmlAudio} webAudio=${r.webAudio} ctx=${r.contextState} gesture=${r.hasBeenActive}`;
}

/**
 * Het oordeel over het GELUID van één reeks, één keer: `true` zodra de eerste chime klinkt,
 * `false` met een reden als hij niet klinkt. Daarna doet dit niets meer — de herhalingen
 * veranderen niets aan "speelde het geluid?".
 */
type SoundReport = (played: boolean, reason?: string) => void;

/**
 * De chime-items van één melding. De bron wordt één keer opgelost (bij de eerste chime) en
 * gedeeld door de herhalingen; de object-URL wordt vrijgegeven wanneer de reeks klaar is.
 */
function chimeFactory(
  payload: ResidentNotificationPayload,
  report: SoundReport,
): { make: () => AudioQueue.AudioItem; release: () => void } {
  const spec = payload.sound!;
  let entryPromise: Promise<SoundCache.SoundEntry | null> | null = null;
  let entrySrc: string | null = null;
  const entry = () => {
    if (!entryPromise) {
      entryPromise = SoundCache.resolveEntry(spec, payload.priority)
        .then((e) => {
          entrySrc = e ? e.src : null;
          return e;
        })
        .catch((err: any) => {
          lastSoundFailure = `sound: resolve wierp — ${(err && err.message) || err}`;
          return null;
        });
    }
    return entryPromise;
  };

  let index = 0;
  const make = (): AudioQueue.AudioItem => {
    const first = index === 0;
    index += 1;
    let inner: AudioQueue.AudioItem | null = null;
    let stopped = false;
    return {
      kind: 'chime',
      async play() {
        const e = await entry();
        // ⚠ In de praktijk komt dit nooit voor: de ladder van `SoundCache` eindigt op het
        // ingebouwde geluid. Het staat er voor het geval die ladder ooit verandert — dan is
        // stilte anders weer onverklaarbaar.
        if (!e || !e.src) {
          if (!lastSoundFailure) lastSoundFailure = 'sound: geen bron uit de cacheladder';
          throw new Error(lastSoundFailure);
        }
        if (stopped) return;
        const builtin = e.origin === 'builtin';
        inner = fileItem({
          kind: 'chime',
          src: e.src,
          // Inhoudsgeadresseerd uit de cache; een ingebouwd geluid krijgt de appversie in zijn
          // sleutel, zodat een nieuwe opname in een nieuwe bundel niet achter de oude blijft.
          nativeKey: e.contentKey
            || `bundled-${spec.key}-${typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : '0'}`,
          getBlob: e.blob ? async () => e.blob : (builtin ? () => bundledBlob(e.src) : undefined),
          usage: 'notification',
          volume: 1.0,
          onAudible: () => {
            if (first) {
              lastSoundFailure = null;
              report(true);
            }
          },
        });
        try {
          await inner.play();
        } catch (err: any) {
          // `src=` zegt waar de bytes vandaan kwamen. Zonder dat veld was "no supported
          // source" op 2026-09-24 niet te onderscheiden van een autoplayblokkade — het bleek
          // een vergiftigde cache-entry (HTML als chime), niet het toestel.
          lastSoundFailure = `sound: play() geweigerd — ${(err && err.name) || 'onbekend'}: ${(err && err.message) || ''} | src=${SoundCache.lastSourceTag()} | ${audioReadinessTag()}`;
          throw err;
        }
      },
      stop() {
        stopped = true;
        if (inner) inner.stop();
      },
    };
  };
  return {
    make,
    release: () => SoundCache.releaseObjectUrl(entrySrc),
  };
}

/** Wat de eerste chime van een reeks deed, als hij niet klonk. */
function soundFailureFor(outcome: AudioQueue.AudioItemOutcome): string {
  switch (outcome) {
    case 'muted':
      return 'sound: muted_call — er liep een gesprek';
    case 'timeout':
      return `sound: geen einde binnen de time-out | ${audioReadinessTag()}`;
    case 'interrupted':
    case 'skipped':
      return 'sound: onderbroken vóór het klonk (hogere prioriteit of weggeklikt)';
    default:
      return lastSoundFailure || `sound: afspelen mislukt | ${audioReadinessTag()}`;
  }
}

/**
 * Een chime die niet klinkt: trillen, en het feit MEETBAAR maken.
 *
 * ── T-2.15: autoplay geblokkeerd ──────────────────────────────────────────
 *
 * De melding blijft VOLLEDIG zichtbaar — geluid is nooit de drager van de boodschap, en een
 * dove bewoner mag om dezelfde reden niets missen. `soundPlayed: false` gaat mee in de ack.
 *
 * ⚠ `NativeBridge.vibrate()`, niet `navigator.vibrate()` los: op native Capacitor loopt dat
 * via `Haptics.vibrate()` en alleen in de browser valt hij terug op `navigator.vibrate?.(200)`
 * — met optional call, dus afwezigheid crasht niets. Niet bij een gesprek: dan trilt de
 * tablet al voor het gesprek, of praat de bewoner.
 */
function vibrateFor(outcome: AudioQueue.AudioItemOutcome) {
  if (outcome !== 'failed' && outcome !== 'timeout') return;
  try {
    void NativeBridge.vibrate();
  } catch {
    /* geen trilmotor — dan niet */
  }
}

/**
 * Speel het geluid van een melding (alle herhalingen) via de wachtrij.
 *
 * @returns `true` zodra het eerste geluid klinkt, `false` bij blokkade, stilte of een gesprek.
 */
export function playFor(payload: ResidentNotificationPayload): Promise<boolean> {
  const spec = payload && payload.sound;
  if (!spec || !spec.key) {
    lastSoundFailure = null;
    return Promise.resolve(false);
  }
  return new Promise<boolean>((resolve) => {
    let reported = false;
    const report: SoundReport = (played) => {
      if (reported) return;
      reported = true;
      resolve(played);
    };
    const chimes = chimeFactory(payload, report);
    const items = AudioQueue.planSequence({ chime: chimes.make, repeat: spec.repeat });
    const handle = AudioQueue.enqueue(items, {
      priority: 'default',
      onItemEnd: (i, outcome) => {
        if (i !== 0 || reported) return;
        if (outcome !== 'played') {
          if (outcome !== 'failed') lastSoundFailure = soundFailureFor(outcome);
          vibrateFor(outcome);
        }
        report(outcome === 'played');
      },
    });
    track(payload.notificationId, handle, chimes.release);
  });
}

/** Houd een reeks bij onder zijn melding, en ruim hem op als hij klaar is. */
function track(id: string, handle: AudioQueue.AudioSequenceHandle, release?: () => void) {
  const prev = sequences.get(id);
  if (prev && prev !== handle) prev.cancel();
  sequences.set(id, handle);
  void handle.done.then(() => {
    if (sequences.get(id) === handle) sequences.delete(id);
    if (release) release();
  });
}

/**
 * De stem als wachtrij-item.
 *
 * `durationMs` is een schatting (≈ 80 ms per teken bij spreeksnelheid 0.9): genoeg zodat een
 * lange zin niet halverwege door de time-out van de wachtrij gestopt wordt, en kort genoeg
 * dat een engine die nooit "klaar" meldt de wachtrij niet lang ophoudt.
 */
function voiceItem(
  payload: ResidentNotificationPayload,
  result: { outcome: SpeakOutcome | null },
): AudioQueue.AudioItem {
  const text = (payload.tts && payload.tts.text) || '';
  return {
    kind: 'tts',
    durationMs: Math.max(2000, Math.min(60_000, text.length * 80)),
    async play() {
      const outcome = await speakNow(payload);
      result.outcome = outcome;
      if (!outcome.spoken) throw new Error(outcome.reason || 'not_spoken');
    },
    stop() {
      speakingFor = null;
      void KeepAliveBridge.stopTts();
    },
  };
}

/**
 * Welke kant-en-klare clip(s) deze melding mag spelen (spraakmeldingen fase 1, T091; contract
 * §2). `null` = geen clip: dan chime(s) → toestelstem, precies zoals vandaag.
 *
 *   • Alleen een clip in de taal die de tablet GAAT uitspreken (`speechTarget`). Een melding
 *     met vertalingen waarvan de tablet een andere tekst kiest, krijgt dus geen clip.
 *   • Een catalogussleutel (geen `h:`) eerst uit de VoiceCache op (sleutel, die taal): de
 *     voorgeladen clip van de site. Pas daarna `tts.audio.url`.
 *   • `mixed` komt van de clip die ECHT speelt (de eerste kandidaat), vóór de chime zou
 *     beginnen; een tweede kandidaat met een andere `mixed` telt niet mee.
 */
function voiceClipPlan(payload: ResidentNotificationPayload): { candidates: VoiceClipRef[]; mixed: boolean } | null {
  const audio = payload && payload.tts ? payload.tts.audio : undefined;
  const target = speechTarget(payload);
  if (!audio || !target || !audio.key) return null;
  const candidates: VoiceClipRef[] = [];
  if (String(audio.key).indexOf('h:') !== 0) {
    const entry = VoiceCache.lookup(String(audio.key), target.lang);
    if (entry) candidates.push(entry);
  }
  if (audio.lang === target.lang && audio.url && audio.checksum
    && !candidates.some((c) => c.url === audio.url)) {
    candidates.push({
      key: String(audio.key),
      lang: audio.lang,
      url: audio.url,
      checksum: audio.checksum,
      durationMs: audio.durationMs,
      mixed: audio.mixed === true,
    });
  }
  if (!candidates.length) return null;
  const mixed = candidates[0].mixed === true;
  return { candidates: candidates.filter((c) => (c.mixed === true) === mixed), mixed };
}

/** Van de uitkomst in de wachtrij naar de ack-velden (FR-0.4). */
function voiceAckFor(
  outcome: AudioQueue.AudioItemOutcome,
  spoken: SpeakOutcome | null,
): { voicePlayed: boolean; voiceSource: ResidentNotificationVoiceSource; voiceReason?: ResidentNotificationVoiceReason } {
  if (outcome === 'played') return { voicePlayed: true, voiceSource: 'native' };
  if (outcome === 'muted') return { voicePlayed: false, voiceSource: 'none', voiceReason: 'muted_call' };
  if (outcome === 'failed' && spoken && spoken.reason === 'no_voice') {
    return { voicePlayed: false, voiceSource: 'none', voiceReason: 'no_audio' };
  }
  if (outcome === 'failed' || outcome === 'timeout') {
    return { voicePlayed: false, voiceSource: 'none', voiceReason: 'play_failed' };
  }
  // Onderbroken of niet aan de beurt gekomen (weggeklikt, hogere prioriteit). Er klonk geen
  // VOLLEDIGE zin; een reden uit de enum past niet, dus geen reden.
  return { voicePlayed: false, voiceSource: 'none' };
}

/**
 * De stemuitkomst naar de server, als TWEEDE `displayed`-ack.
 *
 * Tweede, omdat de stem pas na de chime(s) eindigt en de eerste ack daar niet op mag wachten.
 * De server schrijft de velden ook wanneer de staat niet verandert (en ook na een 409, als de
 * bewoner intussen al weggeklikt had) — zelfde patroon als `mediaError`.
 */
function reportVoice(
  notificationId: string,
  fields: { voicePlayed: boolean; voiceSource: ResidentNotificationVoiceSource; voiceReason?: ResidentNotificationVoiceReason },
) {
  if (voiceReported.has(notificationId)) return;
  const item = items.find((i) => i.notification.notificationId === notificationId);
  if (!item) return;
  voiceReported.add(notificationId);
  queueAck({
    notificationId,
    deliveryId: item.deliveryId,
    state: 'displayed',
    voicePlayed: fields.voicePlayed,
    voiceSource: fields.voiceSource,
    ...(fields.voiceReason ? { voiceReason: fields.voiceReason } : {}),
    webviewVersion: webviewVersion(),
    clientTimestamp: new Date().toISOString(),
  });
}

/**
 * T-2.06 — de melding staat DAADWERKELIJK op het scherm.
 *
 * Aangeroepen vanuit een `requestAnimationFrame` in de dialoog, dus nadat de browser
 * geschilderd heeft — niet bij ontvangst van het commando. Dat onderscheid is de hele reden
 * dat `displayed` naast `pushed` bestaat: `pushed` betekent dat WIJ het kanaal aanspraken,
 * `displayed` dat de TABLET het getoond heeft.
 *
 * Het geluid wordt daarna geprobeerd en de uitkomst gaat mee in dezelfde ack. Er staat een
 * harde bovengrens op dat wachten, zodat een hangende audiobron nooit betekent dat de
 * `displayed`-ack helemaal niet verstuurd wordt — dan zou de vloot juist onmeetbaar worden
 * op het punt waar hij het hardst gemeten moet worden.
 */
const SOUND_ACK_CAP_MS = 5000;

export function presentAndSound(payload: ResidentNotificationPayload): void {
  if (!payload || !payload.notificationId) return;
  const id = payload.notificationId;
  const hasSound = !!(payload.sound && payload.sound.key);
  const hasVoice = !!(payload.tts && payload.tts.text);

  // ⚠ `soundPlayed` in de ack gaat over het GELUID, niet over de stem. Die twee samen in één
  // veld persen zou de vraag "speelde de chime?" onbeantwoordbaar maken op elk toestel waar
  // ook TTS aanstond. De stem heeft sinds fase 0 haar eigen velden (`voicePlayed` e.a.).
  if (!hasSound) {
    // Geen geluid gevraagd: dan is er niets te rapporteren en gaat de ack meteen weg.
    markDisplayed(id);
    if (!hasVoice) return;
  }

  let settled = false;
  let firstStarted = false;
  let cap: ReturnType<typeof setTimeout> | null = null;
  const finish: SoundReport = (soundPlayed, reason) => {
    if (settled || !hasSound) return;
    settled = true;
    if (cap) clearTimeout(cap);
    markDisplayed(id, soundPlayed, soundPlayed === false ? reason || lastSoundFailure || undefined : undefined);
  };
  // Wacht de reeks nog achter een andere (gelijke prioriteit wacht), dan is er na 5 s niets te
  // zeggen over het geluid: de ack gaat zonder `soundPlayed` (NULL = onbekend), niet met een
  // `false` die een geluid als mislukt telt dat gewoon nog moet komen.
  const settle = (soundPlayed: boolean | undefined, reason?: string) => {
    if (settled || !hasSound) return;
    settled = true;
    if (cap) clearTimeout(cap);
    markDisplayed(id, soundPlayed, soundPlayed === false ? reason : undefined);
  };
  if (hasSound) {
    cap = setTimeout(() => {
      if (firstStarted) {
        settle(false, `sound: geen uitkomst binnen ${SOUND_ACK_CAP_MS}ms | ${audioReadinessTag()}`);
      } else {
        settle(undefined);
      }
    }, SOUND_ACK_CAP_MS);
  }

  const chimes = hasSound ? chimeFactory(payload, finish) : null;
  const spoken: { outcome: SpeakOutcome | null } = { outcome: null };
  const repeat = payload.sound ? payload.sound.repeat : 1;

  // ── Fase 1: een kant-en-klare clip, met de terugvalladder (spec §5.7) ──────────────
  const clipPlan = hasVoice ? voiceClipPlan(payload) : null;
  const clipUrls: string[] = [];
  let ladder: (AudioQueue.AudioItem & { result: AudioQueue.LadderResult }) | null = null;
  if (clipPlan) {
    const plan = clipPlan;
    const renderStep: AudioQueue.LadderStep = {
      source: 'render',
      make: async () => {
        for (const ref of plan.candidates) {
          // eslint-disable-next-line no-await-in-loop
          const clip = await VoiceCache.resolveClip(ref);
          if (!clip) continue;
          clipUrls.push(clip.src);
          return fileItem({
            kind: 'clip',
            src: clip.src,
            nativeKey: clip.contentKey,
            getBlob: async () => clip.blob,
            usage: 'notification',
            durationMs: typeof ref.durationMs === 'number' ? ref.durationMs : undefined,
            volume: 1.0,
          });
        }
        return null;
      },
    };
    // `mixed`: de chime zit in de clip. Speelt de clip niet, dan alsnog chime(s) → stem, zoals
    // vandaag. Niet gemixt: de chimes speelden al vóór de ladder, dus enkel de stem.
    const legacy = (): AudioQueue.AudioItem => {
      const voice = voiceItem(payload, spoken);
      if (!plan.mixed || !chimes) return voice;
      const n = Math.max(1, Math.min(10, Math.floor(Number(repeat) || 1)));
      const list: AudioQueue.AudioItem[] = [];
      for (let i = 0; i < n; i += 1) list.push(chimes.make());
      list.push(voice);
      return AudioQueue.chainItem(list, 'tts');
    };
    const ttsText = (payload.tts && payload.tts.text) || '';
    const first = plan.candidates[0];
    const clipMs = first && typeof first.durationMs === 'number' ? first.durationMs : 5000;
    ladder = AudioQueue.ladderItem(
      [renderStep, { source: 'native', make: legacy }],
      {
        kind: 'clip',
        durationMs: clipMs + Math.max(2000, Math.min(60_000, ttsText.length * 80))
          + (plan.mixed && chimes ? 3000 * Math.max(1, Number(repeat) || 1) : 0),
      },
    );
  }
  const ladderRef = ladder;

  const items = AudioQueue.planSequence({
    chime: chimes ? chimes.make : null,
    repeat,
    voice: hasVoice && !ladderRef ? voiceItem(payload, spoken) : null,
    clip: ladderRef && clipPlan ? { item: ladderRef, mixed: clipPlan.mixed } : null,
  });

  const handle = AudioQueue.enqueue(items, {
    priority: 'default',
    onItemStart: (i, item) => {
      if (i === 0 && item.kind === 'chime') firstStarted = true;
    },
    onItemEnd: (i, outcome, item) => {
      if (ladderRef && item === ladderRef) {
        reportVoice(id, AudioQueue.ladderVoiceAck(ladderRef.result, outcome));
        // Gemixt: de clip WAS het geluid. Speelde de terugval-chime, dan meldde die zelf al
        // (`onAudible` in `chimeFactory`); `finish` is idempotent.
        if (clipPlan && clipPlan.mixed && i === 0) {
          if (ladderRef.result.played === 'render') finish(true);
          else if (outcome !== 'played') {
            vibrateFor(outcome);
            finish(false, soundFailureFor(outcome));
          }
        }
        return;
      }
      if (item.kind === 'tts') {
        reportVoice(id, voiceAckFor(outcome, spoken.outcome));
        return;
      }
      if (i !== 0 || settled) return;
      if (outcome === 'played') {
        // Speelde tot het einde zonder dat `onAudible` kwam (kan op een WebView zonder
        // Promise uit `play()` niet; staat hier voor de volledigheid).
        finish(true);
        return;
      }
      vibrateFor(outcome);
      finish(false, soundFailureFor(outcome));
    },
  });
  track(id, handle, () => {
    if (chimes) chimes.release();
    for (const u of clipUrls) VoiceCache.releaseObjectUrl(u);
  });
}

/** Alleen voor tests. */
export function __resetPhase2() {
  dismissed.clear();
  try {
    localStorage.removeItem(DISMISSED_STORAGE_KEY);
  } catch {
    /* niets aan te doen, en niets dat hierop hoeft te wachten */
  }
  mediaErrorsReported.clear();
  voiceReported.clear();
  claimed = null;
  speakingFor = null;
  ttsLanguage = 'nl';
  stopSound();
}
